/* THE CONSOLE SESSION. A 401 on any account route ends it: the reader is sent to sign in with the place they were and a
   line saying the session ended, and signing in returns them there. Only a path on this origin is followed. */
import { problemLine, problemOf, type Refusal } from './state.ts'

export const SIGN_IN = '/sign-in'

/** Only a path on this origin, and never the sign-in page itself, is followed after signing in. */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\') && !next.startsWith(SIGN_IN) ? next : '/'
}

/** The sign-in address for a reader at `here`; `ended` when the server ended the session rather than the reader. */
export function signInPath(here: string, ended: boolean): string {
  const q = new URLSearchParams()
  const next = safeNext(here)
  if (next !== '/') q.set('next', next)
  if (ended) q.set('ended', '1')
  const s = q.toString()
  return s ? `${SIGN_IN}?${s}` : SIGN_IN
}

/** The line above the sign-in form when the server ended the session. */
export function signInNotice(query: URLSearchParams): string | null {
  if (query.get('ended') !== '1') return null
  const next = safeNext(query.get('next'))
  return next === '/' ? 'Your console session ended, or this browser has none yet. Sign in to carry on.' : `Your console session ended, or this browser has none yet. Sign in to go back to ${next}.`
}

const TOKEN = /^dmr_ac_[A-Za-z0-9_-]{43}$/

/** A sentence when the pasted value cannot be an account token; null when it can. */
export function tokenProblem(value: string): string | null {
  if (TOKEN.test(value)) return null
  if (value.startsWith('dmr_ag_')) return 'That is a run’s token, which drives one run only. Sign in with an account token: it starts with dmr_ac_.'
  return 'That is not an account token. An account token starts with dmr_ac_ and is the one Damaros gave you, or one you minted or rotated since.'
}

/** What the sign-in form says when the server refuses: its own sentence, and what to do next. */
export function signInError(e: Refusal): string {
  if (e.status === 401) return `The server did not accept that token: it may have expired (account tokens last 90 days), been revoked or rotated, or belong to another server. Paste a token that still works, or ask the operator of this server for one. The server said: ${e.message}`
  if (e.status === 429) return `Too many attempts. Wait ${e.retryAfter ? `${e.retryAfter} seconds` : 'a minute'}, then try again. The server said: ${e.message}`
  if (e.status === 0 || e.status === undefined) return `${e.message} Then sign in again.`
  return `${e.message} If it keeps refusing, ask the operator of this server.`
}

/** Where signing out leads, and what it says. Done, or refused with a 401 (the session had already ended): the plain
    sign-in page, with no line about an ended session and nothing to go back to. Any other refusal keeps the reader
    where they are, with the server's sentence and the next step. */
export function signOutOutcome(e: Refusal | null): { to: string | null; line: string | null } {
  if (e === null || e.status === 401) return { to: SIGN_IN, line: null }
  return { to: null, line: problemLine(problemOf(e)) }
}
